import type {
  CmmdRelayControlRoute,
  CreateCmmdRelayControlRouteParams,
} from "@cmmd-forge/contracts";
import {
  generateRelayChannelNonce,
  generateRelayEndpointKeyPair,
  RELAY_PAYLOAD_SUITE,
  relayEndpointKeyId,
  serializeRelayEndpointPublicKey,
  type RelayPayloadDigestProvider,
  type RelayPayloadRandomSource,
} from "@cmmd-forge/shared/relayPayloadChannel";

export interface RelayControlEndpoint {
  readonly keyPair: CryptoKeyPair;
  readonly keyId: string;
  readonly publicKey: string;
  readonly channelNonce: string;
}

export interface SecuredRelayControlRoute extends CmmdRelayControlRoute {
  readonly protocolVersion: 4;
  readonly runtimeId: string;
  readonly channelId: string;
  readonly hpkeSuite: typeof RELAY_PAYLOAD_SUITE;
  readonly runtimeEndpointKeyId: string;
  readonly runtimeEndpointPublicKey: string;
  readonly controlEndpointKeyId: string;
  readonly controlEndpointPublicKey: string;
  readonly controlChannelNonce: string;
  readonly controlEndpointKeyPair: CryptoKeyPair;
  readonly payloadDigestProvider?: RelayPayloadDigestProvider;
  readonly payloadRandomSource?: RelayPayloadRandomSource;
}

export class RelayControlRouteSecurityFailure extends Error {
  override readonly name = "RelayControlRouteSecurityFailure";
}

export async function createRelayControlEndpoint(
  randomSource?: RelayPayloadRandomSource,
): Promise<RelayControlEndpoint> {
  const keyPair = await generateRelayEndpointKeyPair(randomSource);
  const publicKey = await serializeRelayEndpointPublicKey(keyPair.publicKey);
  return {
    keyPair,
    publicKey,
    keyId: relayEndpointKeyId("control", publicKey),
    channelNonce: await generateRelayChannelNonce(randomSource),
  };
}

export function relayControlEndpointRequest(
  endpoint: RelayControlEndpoint,
): Pick<
  CreateCmmdRelayControlRouteParams,
  "hpkeSuite" | "controlEndpointKeyId" | "controlEndpointPublicKey" | "controlChannelNonce"
> {
  return {
    hpkeSuite: RELAY_PAYLOAD_SUITE,
    controlEndpointKeyId: endpoint.keyId,
    controlEndpointPublicKey: endpoint.publicKey,
    controlChannelNonce: endpoint.channelNonce,
  };
}

export function secureRelayControlRoute(input: {
  readonly route: CmmdRelayControlRoute;
  readonly runtimeId: string;
  readonly endpoint: RelayControlEndpoint;
  readonly digestProvider?: RelayPayloadDigestProvider;
  readonly randomSource?: RelayPayloadRandomSource;
}): SecuredRelayControlRoute {
  const route = input.route;
  if (
    route.protocolVersion !== 4 ||
    route.hpkeSuite !== RELAY_PAYLOAD_SUITE ||
    !route.channelId ||
    !route.runtimeEndpointKeyId ||
    !route.runtimeEndpointPublicKey ||
    route.controlEndpointKeyId !== input.endpoint.keyId ||
    route.controlEndpointPublicKey !== input.endpoint.publicKey ||
    route.controlChannelNonce !== input.endpoint.channelNonce
  ) {
    throw new RelayControlRouteSecurityFailure(
      "Forge could not establish a verified end-to-end encrypted runtime route.",
    );
  }
  return {
    ...route,
    runtimeId: input.runtimeId,
    protocolVersion: 4,
    channelId: route.channelId,
    hpkeSuite: RELAY_PAYLOAD_SUITE,
    runtimeEndpointKeyId: route.runtimeEndpointKeyId,
    runtimeEndpointPublicKey: route.runtimeEndpointPublicKey,
    controlEndpointKeyId: input.endpoint.keyId,
    controlEndpointPublicKey: input.endpoint.publicKey,
    controlChannelNonce: input.endpoint.channelNonce,
    controlEndpointKeyPair: input.endpoint.keyPair,
    ...(input.digestProvider ? { payloadDigestProvider: input.digestProvider } : {}),
    ...(input.randomSource ? { payloadRandomSource: input.randomSource } : {}),
  };
}
